import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCourseById, getContentById } from '../data/courses';
import './CourseIndex.css';

const Topic = ({ topic, isActive, onClick }) => (
  <li className={`topic ${isActive ? 'active' : ''}`} onClick={onClick}>
    {topic.title}
  </li>
);

const Lesson = ({ lesson, selectedTopicId, onTopicSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="lesson">
      <div 
        className="lesson-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
        {lesson.title}
      </div>
      {isExpanded && (
        <ul className="topics-list">
          {lesson.topics.map((topic) => (
            <Topic
              key={topic.id}
              topic={topic}
              isActive={topic.id === selectedTopicId}
              onClick={() => onTopicSelect(topic)}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const Module = ({ module, selectedTopicId, onTopicSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="module">
      <div 
        className="module-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
        {module.title}
      </div>
      {isExpanded && (
        <ul className="lessons-list">
          {module.lessons.map((lesson) => (
            <Lesson
              key={lesson.id}
              lesson={lesson}
              selectedTopicId={selectedTopicId}
              onTopicSelect={onTopicSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const ContentLoader = ({ contentId }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        // Simulate API call with timeout
        await new Promise(resolve => setTimeout(resolve, 500));
        const contentData = getContentById(contentId);
        
        if (!contentData) {
          throw new Error('Content not found');
        }
        
        setContent(contentData);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [contentId]);

  if (loading) {
    return (
      <div className="content-loading">
        <div className="loading-spinner"></div>
        <p>Loading content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-error">
        <h2>Error Loading Content</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="content-text">
      <p>{content.text}</p>
      {content.sections.map((section, index) => (
        <div key={index}>
          <h2>{section.title}</h2>
          <p>{section.content}</p>
        </div>
      ))}
    </div>
  );
};

const CourseIndex = () => {
  const { courseId } = useParams();
  const course = getCourseById(courseId);
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!course) {
    return (
      <div className="course-container">
        <div className="course-content">
          <h2>Course not found</h2>
        </div>
      </div>
    );
  }

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
  };

  return (
    <div className="course-container">
      <div className="course-index">
        <h2>{course.title}</h2>
        <ul className="modules-list">
          {course.modules.map((module) => (
            <Module
              key={module.id}
              module={module}
              selectedTopicId={selectedTopic?.id}
              onTopicSelect={handleTopicSelect}
            />
          ))}
        </ul>
      </div>

      <div className="course-content">
        {selectedTopic ? (
          <>
            <h1>{selectedTopic.title}</h1>
            <ContentLoader contentId={selectedTopic.contentId} />
          </>
        ) : (
          <div className="placeholder">
            <h2>Select a topic to begin learning</h2>
            <p>Choose a topic from the course index to view its content.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseIndex; 